import { uniqBy } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { useTranslation } from '@/components/i18n/Translator';

import {
  ChainlitContext,
  threadHistoryState,
  useChatMessages
} from '@chainlit/react-client';

import { foldersState } from '@/state/folders';

import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu
} from '@/components/ui/sidebar';

import { ThreadList } from './ThreadList';

const BATCH_SIZE = 35;
let _scrollTop = 0;

export function ThreadHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const apiClient = useContext(ChainlitContext) as any;
  const { firstInteraction, messages, threadId } = useChatMessages();
  const [threadHistory, setThreadHistory] = useRecoilState(threadHistoryState);
  const [error, setError] = useState<string>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  // Restore scroll position
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = _scrollTop;
    }
  }, []);

  // Handle first interaction
  useEffect(() => {
    const handleFirstInteraction = async () => {
      if (!firstInteraction) return;

      const isActualResume =
        firstInteraction === 'resume' &&
        messages[0]?.output.toLowerCase() !== 'resume';

      if (isActualResume) return;

      await fetchThreads(undefined, true);

      // If started from a project page, tag the new thread with that folder
      const pendingProjectId = new URLSearchParams(location.search).get('projectId');
      if (threadId && pendingProjectId && apiClient?.moveThreadToFolder) {
        await apiClient.moveThreadToFolder(threadId, pendingProjectId);
        // Update the thread in local state
        setThreadHistory((prev) => {
          if (!prev?.threads) return prev;
          const idx = prev.threads.findIndex((t) => t.id === threadId);
          if (idx === -1) return prev;
          const next = { ...prev, threads: [...prev.threads] };
          next.threads[idx] = {
            ...next.threads[idx],
            metadata: { ...next.threads[idx].metadata, folderId: pendingProjectId }
          };
          return next;
        });
      }

      if (threadId && location.pathname === '/') {
        navigate(`/thread/${threadId}`);
      }
    };

    handleFirstInteraction();
  }, [firstInteraction]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollHeight, clientHeight, scrollTop } = scrollRef.current;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 10;

    _scrollTop = scrollTop;
    setShouldLoadMore(atBottom);
  };

  const fetchThreads = async (
    cursor?: string | number,
    isLoadingMore = false
  ) => {
    try {
      setIsLoadingMore(!!cursor || isLoadingMore);
      setIsFetching(!cursor && !isLoadingMore);

      const { pageInfo, data } = await apiClient.listThreads(
        { first: BATCH_SIZE, cursor },
        {}
      );

      setError(undefined);

      // Prevent duplicate threads
      const allThreads = uniqBy(
        cursor ? threadHistory?.threads?.concat(data) : data,
        'id'
      );

      if (allThreads) {
        setThreadHistory((prev) => ({
          ...prev,
          pageInfo,
          threads: allThreads
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setShouldLoadMore(false);
      setIsLoadingMore(false);
      setIsFetching(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (!isFetching && !threadHistory?.threads && !error) {
      fetchThreads();
    }
  }, [isFetching, threadHistory, error]);

  // Handle infinite scroll
  useEffect(() => {
    if (threadHistory?.pageInfo) {
      const { hasNextPage, endCursor } = threadHistory.pageInfo;

      if (shouldLoadMore && !isLoadingMore && hasNextPage && endCursor) {
        fetchThreads(endCursor);
      }
    }
  }, [shouldLoadMore, isLoadingMore, threadHistory]);

  // Filter out threads that belong to a folder
  const unfiledThreadHistory = threadHistory
    ? {
        ...threadHistory,
        threads: threadHistory.threads?.filter(
          (t) => !t.metadata?.folderId
        ),
        timeGroupedThreads: threadHistory.timeGroupedThreads
          ? Object.fromEntries(
              Object.entries(threadHistory.timeGroupedThreads).map(
                ([group, threads]) => [
                  group,
                  threads.filter((t) => !t.metadata?.folderId)
                ]
              )
            )
          : undefined
      }
    : undefined;

  return (
    <SidebarContent onScroll={handleScroll} ref={scrollRef} className="pb-4">
      <SidebarGroup className="py-0 px-0">
        <SidebarGroupLabel className="h-6 px-3 text-xs font-semibold uppercase tracking-widest text-foreground/50">
          {t('threadHistory.sidebar.yourChats')}
        </SidebarGroupLabel>
        <SidebarMenu>
          {unfiledThreadHistory ? (
            <div id="thread-history" className="flex-grow">
              <ThreadList
                threadHistory={unfiledThreadHistory}
                error={error}
                isFetching={isFetching}
                isLoadingMore={isLoadingMore}
              />
            </div>
          ) : null}
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
  );
}
